import { Suspense } from 'react';
import type { PropsWithChildren } from 'react';

import { PageLoader } from 'src/components/PageLoader';
import { APP_ROUTES } from 'src/constants/routes';
import { detectEnvironmentPlatform } from 'src/features/config/AppEnvironmentDetector/utils';
import { ThemeRegistry } from 'src/features/config/ThemeRegistry';

import {
  TeamsErrorThemeRegistry,
  ZoomThemeRegistry,
} from './platformThemeRegistries';
import { ThemeLoadFallback } from './ThemeLoadFallback';

// The error pages (/503, /500, 404) live outside the platform routes, so they
// get no theme from their route. Each platform uses the same theme source as its
// own routes: Teams and Zoom follow the client's theme, Webex and Meet follow the
// OS, and the web app is light only. Every branch provides the global ThemeContext,
// which is what useThemeMode reads on these paths.
export const ErrorPageThemeRegistry = ({ children }: PropsWithChildren) => {
  // The catch-all route also matches "/", where NotFound renders nothing while
  // AppEnvironmentDetector redirects to the platform. Do not start a platform SDK
  // (or touch the html dark class) for a page that is about to unmount.
  if (window.location.pathname === APP_ROUTES.HOME) {
    return <>{children}</>;
  }

  switch (detectEnvironmentPlatform()) {
    case 'teams':
      return (
        <ThemeLoadFallback page={children}>
          <Suspense fallback={<PageLoader />}>
            <TeamsErrorThemeRegistry>{children}</TeamsErrorThemeRegistry>
          </Suspense>
        </ThemeLoadFallback>
      );
    case 'zoom':
      return (
        <ThemeLoadFallback page={children}>
          <Suspense fallback={<PageLoader />}>
            <ZoomThemeRegistry>{children}</ZoomThemeRegistry>
          </Suspense>
        </ThemeLoadFallback>
      );
    case 'webex':
    case 'google-meet':
      return <ThemeRegistry>{children}</ThemeRegistry>;
    default:
      return <ThemeRegistry forceTheme="light">{children}</ThemeRegistry>;
  }
};
